/**
 * Global app state (Zustand + persisted to device storage).
 *
 * Single source of truth for tasks, weekly cycles, subscription, scan
 * tokens, settings, and the active screen of the onboarding funnel.
 *
 * NOTE on sync: the v2 concept mock used GunDB over public P2P relays.
 * That approach was deliberately rejected — unauthenticated public relays,
 * plaintext shared-key rooms, and premium status delivered via sync are not
 * acceptable for a paid product. The seam for real multi-device sync is the
 * `persist` storage adapter (swap for a Firestore-backed adapter behind
 * Firebase Auth). See docs/ARCHITECTURE.md §6.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ActionStep,
  ArchivedTask,
  ClassifiedTask,
  ImportStrategy,
  PaidPlan,
  Remediation,
  ScanAttachment,
  Subscription,
  Task,
  WeekRecord,
  ZoneId,
} from '../types';
import { FREE_PLANS, FREE_SCANS, compareTasks, weekLabel } from '../types';
import type { StorePrices } from '../billing/prices';
import { normaliseHandedTo } from '../engine/insights';
import { NO_NEW_STEP_NOTICE, pickExtraStep, planFallbackNotice } from '../engine/extraStep';
import { hintFor, isRepeatRule, isUntouchedInstance, nextInstance, seriesId, type RepeatRule } from '../engine/recurrence';
import { LocalProvider, resolveProvider } from '../engine';
import { AI_TIMEOUTS, withTimeout } from '../engine/timeout';
import { splitQuickTasks } from '../engine/dictationText';
import { mergeNewItems, mergeNotice, sameItem, type UncertainMatch } from '../engine/dedupe';
import { parseStepInput, promoteStepFrom } from '../engine/manualSteps';
import { applyPlanRevision } from '../engine/planUpdate';
import { localDateKey } from '../engine/affirmation';
import { autoTagFor, findTagByName, nextTagColor, type SortMode, type Tag, type TagKind } from '../engine/tags';
import { orderGroupKey, reorderInGroup } from '../engine/manualOrder';
import { aiAllowed, type AiConsent, type PendingAiAction } from '../engine/aiConsent';
import { purgeAllEntries, purgeBin, purgeEntry, toBin, type BinEntry } from '../engine/bin';
import { exportSummary, fatiguedTasks, rolloverWeek, type ExportFormat } from '../engine/weekly';
import { bindWeek, type WeekSnapshot } from '../engine/notebook';
import { isPremium, reconcile } from '../billing/mockBilling';
import {
  billingSelfTest as runBillingSelfTest,
  billingStorePrices as loadStorePrices,
  resolveBilling,
} from '../billing/storeBilling';
import { describeBillingError, isCancelled } from '../billing/revenueCatBilling';
import { grantSubscription, preserveGrant } from '../billing/grantGuard';
import { shouldAskForReview } from '../engine/review';
import type { SyncSnapshot, SyncStatus } from '../sync/syncCore';

export type Screen = 'hook' | 'dump' | 'sorting' | 'matrix' | 'mastery';
export type ThemePref = 'system' | 'light' | 'dark';
export type TextScale = 'compact' | 'default' | 'relaxed';

let idCounter = 0;
const uid = () =>
  `${Date.now().toString(36)}-${(idCounter++).toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 7)}`;

interface AppState {
  screen: Screen;
  onboarded: boolean;
  /** Active week's tasks (open + completed until rollover). */
  tasks: Record<string, Task>;
  /** Completed-task snapshots for the current week's analytics. */
  archive: ArchivedTask[];
  /** Closed weekly cycles (Mastery log). */
  weekHistory: WeekRecord[];
  subscription: Subscription;
  /** Camera/photo scan tokens remaining on the free tier. */
  scansRemaining: number;
  /** Free AI Action Plan samples remaining (see FREE_PLANS). */
  freePlansRemaining: number;
  importStrategy: ImportStrategy;
  /** Runtime Gemini key (Settings → stored on-device only). */
  geminiKey: string;
  /** Pending image attachments for the next Magic Sort. */
  scans: ScanAttachment[];
  paywallOpen: boolean;
  settingsOpen: boolean;
  rolloverOpen: boolean;
  scanPackOpen: boolean;
  /** One-shot: the board's camera button asked the brain-dump screen to
   *  open the scanner as soon as it mounts (10 Oct 2026). Transient. */
  scanOnOpen?: boolean;
  expandingTaskId?: string;
  providerNotice?: string;
  /** One-shot summary of the last smart merge (shown on the matrix). */
  importNotice?: string;
  /**
   * Possible rewordings awaiting the user's judgment ("is this the same
   * task?"). Better to ask than to output false data — persisted so an
   * unanswered question survives an app restart.
   */
  mergeReview: UncertainMatch[];
  /** Epoch ms of the last Start New Week — the sync merge's week boundary. */
  weekStartedAt: number;
  /** The Bin: deleted tasks held 7 days, restorable (engine/bin.ts). Synced. */
  bin: BinEntry[];
  /** Tags: projects / roles / deliverables (engine/tags.ts). Synced. */
  tags: Record<string, Tag>;
  /** Matrix filter: show only tasks carrying this tag (undefined = all). */
  activeTagId?: string;
  /** Matrix sort within zones. */
  sortMode: SortMode;
  /** Date key of the last app OPEN — each day's first open defaults the
   *  sort to 'tag' (project view); an app left open overnight is never
   *  touched, and same-day reopens keep the user's choice. */
  lastOpenedOn: string;
  /** Appearance: follows OS by default; user can pin light or dark. */
  theme: ThemePref;
  /** Accessibility: base text size for the whole app. */
  textScale: TextScale;
  /** Accessibility: stop looping/pulsing animations (attention-friendly). */
  calmMotion: boolean;
  /** Accessibility: easier-reading font (Atkinson Hyperlegible). */
  easyFont: boolean;
  /** Focus view: show only Zone 1 + top of Zone 2 on the matrix. */
  focusMode: boolean;
  /** Today lens (V2 §1): filter the matrix to today's work. Per-device
   *  ergonomics like `theme` — persisted locally, NEVER synced. */
  todayLensOn: boolean;
  /** Brain-dump draft, persisted as typed (V2 §4a-capture): a misclick,
   *  back-press, or app switch never loses a typed list. Local-only,
   *  cleared on successful organize. */
  dumpDraft: string;
  /** Native reminders (no-op on web). */
  weeklyReminder: boolean;
  dailyNudge: boolean;
  /** Cloud sync (Firebase adapter — 'disabled' until configured). */
  syncStatus: SyncStatus;
  syncEmail?: string;
  /** Last sync/sign-in failure detail — surfaced verbatim in Settings so a
   *  phone can debug itself (no console access on device). Transient. */
  syncError?: string;
  /** In-flight sign-in progress ("opening browser…", "waiting…") — shown in
   *  Settings so multi-step native sign-in is never a silent black box. */
  syncNotice?: string;
  /** Apple sign-in that would CREATE a new account, held for the user's
   *  choice (10 Oct 2026 — syncCore.appleSignInDecision). Transient; never
   *  persisted or synced. `email` is what Apple shared (null = hidden). */
  appleNewAccount?: { email: string | null };
  /** Sign-in providers on the signed-in account (e.g. google.com,
   *  apple.com) — drives "Also allow Sign in with Apple". Transient. */
  syncProviders?: string[];
  /** The account last signed in ON THIS DEVICE (13 Oct 2026) — powers the
   *  one-tap "Sign in as …" on the sync chip and the start screen, so a
   *  signed-out user never hunts through Settings. Device-local: persisted,
   *  NEVER synced (snapshotOf lists its keys explicitly), cleared by Erase
   *  and Delete-my-cloud-data. `provider`: google.com | apple.com | password. */
  lastAccount?: { email: string; provider: string };
  /** AI processing consent (17 Oct 2026, App Review 5.1.2(i)). Device-local:
   *  persisted, NEVER synced, cleared by Erase. Absent = never asked = not
   *  granted. Every AI action checks it first; GeminiProvider checks again
   *  at the network layer (engine/aiConsent.ts). */
  aiConsent?: AiConsent;
  /** The AI action waiting on the consent sheet. Ephemeral — not persisted. */
  aiConsentPrompt?: PendingAiAction;
  /** Last store-billing outcome ("Store error: …", "Purchases restored"),
   *  shown verbatim on the paywall / scan-pack / Settings surfaces (§2: no
   *  silent purchase failure). Ephemeral — NOT persisted, NOT synced. */
  billingNotice?: string;
  /** True while a purchase / restore is in flight (buttons disable). */
  billingBusy: boolean;
  /** Step-by-step billing narration (last 12 lines): "configure: started",
   *  "getOfferings: ok (412 ms)", "purchase: FAILED after 30000 ms — …".
   *  A step that shows "started" with no follow-up IS the diagnosis (§2).
   *  Ephemeral — NOT persisted, NOT synced. */
  billingTrace: string[];
  /** Store price labels keyed by plan, fetched when the paywall opens (ephemeral; PLANS is the fallback). */
  storePrices: StorePrices;
  /** Weekly Insights (web-first, 18 Oct 2026) — device-local, persisted,
   *  never synced: the one-time ★ tip, and which week reviews this device
   *  has rated (so the buttons don't nag). */
  coreTipDismissed?: boolean;
  insightFeedbackGiven: Record<string, 'up' | 'down'>;
  /** Which task's hand-off editor is open (ephemeral). */
  handOffEditing?: string;
  /** One line under a task's plan after an AI plan action: nothing new to
   *  add, or WHY it fell back to on-device steps (ephemeral; §2). */
  planNotice?: { taskId: string; text: string };
  /** Timestamp of the last snapshot pushed to / applied from the cloud. */
  syncUpdatedAt: number;
  /** uid of the account whose cloud copy this device last attached to (account-switch guard, SEV-3). */
  syncUid?: string;
  /**
   * Timestamp of the last LOCAL user edit to synced data. Compared against
   * syncUpdatedAt to detect true sync conflicts (both sides changed) vs a
   * merely stale device that should take the cloud copy.
   */
  lastLocalEditAt: number;
  /** First-run timestamp — review nudges wait for real tenure. */
  installedAt: number;
  /** Store-review ask bookkeeping (policy in engine/review.ts). */
  reviewAskCount: number;
  lastReviewAskAt: number;
  /** One-time gentle pointer to the privacy controls in Settings. */
  privacyNudgeSeen: boolean;
  /** The Notebook (V2 §11): full-fidelity snapshots of closed weeks,
   *  keyed by WeekRecord id. Preserve-first — written at every Start New
   *  Week; local-persisted AND pushed to users/{uid}/notebook/{weekId}
   *  (own docs, NOT the sync snapshot — unbounded history must never
   *  bloat the 1MB snapshot doc). */
  boundWeeks: Record<string, WeekSnapshot>;
  /** Daily affirmation card: a data-grounded morning note (on-device). */
  dailyAffirmation: boolean;
  /** Local date key of the last removal (X) — hides the card for that day. */
  affirmationDismissedOn: string;
  /** Local date key while collapsed to a slim strip — resets each new day. */
  affirmationCollapsedOn: string;
  /**
   * Wrap-Up (fatigue) banner collapsed to a one-line strip. STICKY, not
   * per-day: unlike the affirmation this is a standing signal, not a daily
   * one, and on a phone the full card costs real board space (founder,
   * 24 Aug 2026). Device-local — never synced; how much screen you want on
   * THIS phone is not a fact about your week (WRITE_PATHS W5).
   */
  fatigueCollapsed: boolean;

  // --- actions ---
  setScreen(screen: Screen): void;
  setImportStrategy(strategy: ImportStrategy): void;
  attachScan(scan: ScanAttachment): void;
  removeScan(index: number): void;
  /** In-place edit (e.g. rotate) of an attached scan — consumes NO token. */
  updateScan(index: number, scan: ScanAttachment): void;
  quickAdd(text: string, zone: ZoneId): void;
  /** `onDevice`: sort with the local engine only — nothing leaves the device
   *  (the consent sheet's "Sort on this device instead"). */
  runMagicSort(rawInput: string, opts?: { onDevice?: boolean }): Promise<void>;
  /** User's verdict on a review item: add as to-do, record as done, or skip. */
  resolveMergeReview(index: number, action: 'add' | 'done' | 'skip'): void;
  drillDown(taskId: string): Promise<void>;
  addExtraStep(taskId: string): Promise<void>;
  /**
   * Living plans (premium): merge an update note ("what changed") into an
   * existing plan. Completed steps are NEVER rewritten — see planUpdate.ts.
   */
  updatePlan(taskId: string, note: string): Promise<void>;
  toggleStep(taskId: string, stepId: string): void;
  /** Manual plan editing — free tier (typing your own plan is never paywalled). */
  addManualStep(taskId: string, rawText: string): void;
  updateStep(taskId: string, stepId: string, rawText: string): void;
  /** Rename a task — scans mis-spell handwriting; the fix must be one tap away. */
  updateTaskText(taskId: string, text: string): void;
  /** Set, change, or clear ('' clears) a task's target date. `at` is the
   *  optional machine timestamp (chips/pickers); free text passes none. */
  updateTaskDeadline(taskId: string, hint: string, at?: number): void;
  deleteStep(taskId: string, stepId: string): void;
  moveStep(taskId: string, stepId: string, direction: -1 | 1): void;
  /** A step that outgrew its parent becomes its own task (same zone). */
  promoteStep(taskId: string, stepId: string): void;
  toggleTaskDone(taskId: string): void;
  /** Recurring tasks: set or clear (undefined) the repeat rule. */
  setTaskRepeat(taskId: string, rule: RepeatRule | undefined): void;
  /** Tap cycle: blank → in-progress → done → blank. Progress made visible. */
  cycleTaskState(taskId: string): void;
  cycleStepState(taskId: string, stepId: string): void;
  /** Working note on a task ('' clears) — safe task-switching. */
  updateTaskNote(taskId: string, note: string): void;
  /** Create a tag (deduped by name, case-insensitive); returns its id. */
  createTag(name: string, kind: TagKind): string | undefined;
  /** Rename; if the new name matches another tag, MERGE into it (tasks
   *  move over, this tag is retired) — fixes OCR-duplicate tags. */
  renameTag(tagId: string, name: string): void;
  setTagColor(tagId: string, color: string): void;
  deleteTag(tagId: string): void;
  toggleTaskTag(taskId: string, tagId: string): void;
  /** Weekly Insights ★ — "this is really my job". Bumps editedAt so the
   *  per-tag merge carries it. */
  setTagCore(tagId: string, core: boolean): void;
  dismissCoreTip(): void;
  /** Zone 3 hand-off: who has it, and when Today should ask again
   *  (reuses deferredUntil — the Today lens already honours it). */
  handOff(taskId: string, name: string, checkBackAt: number | undefined): void;
  /** "Still with them" — a new check-back date only. */
  setCheckBack(taskId: string, checkBackAt: number | undefined): void;
  /** "Take it back" — clears the name and the date. */
  takeBack(taskId: string): void;
  openHandOff(taskId: string | undefined): void;
  /** 👍 / 👎 on a week review, with an optional line. Stored under the
   *  account (users/{uid}/insightFeedback/{weekId}); remembered on the device. */
  rateWeekReview(weekId: string, useful: 'up' | 'down', comment?: string): void;
  setActiveTag(tagId: string | undefined): void;
  setSortMode(mode: SortMode): void;
  deleteTask(taskId: string): void;
  /** Bin: put a deleted task back on the board, everything intact. */
  restoreFromBin(taskId: string): void;
  /** Bin: "Delete forever" on one entry — redacted, tombstone kept. */
  purgeFromBin(taskId: string): void;
  /** Bin: discard all held deletions immediately (tombstones kept). */
  emptyBin(): void;
  moveTask(taskId: string, zone: ZoneId): void;
  /** Lift-and-shift an UNDATED task up/down within its zone (V2 §4e). */
  moveTaskInZone(taskId: string, direction: -1 | 1): void;
  remediate(taskId: string, action: Remediation): void;
  startWeekRollover(): void;
  /** Merge cloud-fetched week snapshots into the local shelf (Notebook). */
  hydrateNotebook(snaps: WeekSnapshot[]): void;
  buildExport(format: ExportFormat): string;
  subscribe(plan: PaidPlan): Promise<void>;
  /** Alpha program: applies the server-verified lifetime grant locally. */
  applyAlphaGrant(): void;
  cancelSubscription(): Promise<void>;
  purchaseScanPack(): Promise<void>;
  /** Re-read store entitlements (reinstall, new phone). Routed through preserveGrant. */
  restorePurchases(): Promise<void>;
  /** Settings diagnostics: configure + fetch offerings, narrated; no purchase. */
  billingSelfTest(): Promise<void>;
  /** Append one narration line (kept to the last 12). */
  noteBillingStep(line: string): void;
  /** A provider-originated entitlement (identify at sign-in, renewal,
   *  refund, boot refresh). The ONLY way provider news enters state. */
  applyBillingUpdate(incoming: Subscription): void;
  clearBillingNotice(): void;
  openPaywall(): void;
  closePaywall(): void;
  openSettings(): void;
  closeSettings(): void;
  openRollover(): void;
  closeRollover(): void;
  openScanPack(): void;
  /** Board quick-add camera → brain dump (merge) with the scanner opening at once. */
  openScanFromBoard(): void;
  /** DumpScreen reads-and-clears the one-shot flag. */
  consumeScanOnOpen(): boolean;
  closeScanPack(): void;
  clearImportNotice(): void;
  /** Dismiss the AI-engine notice (shown on the matrix when Organize fell back). */
  clearProviderNotice(): void;
  /** "Agree and continue": record consent, then run the action that asked. */
  grantAiConsent(): void;
  /** "Not now": close the sheet; nothing is sent; the next AI tap asks again. */
  dismissAiConsentPrompt(): void;
  /** Settings → Privacy & Data toggle. Off = declined (revocation, 5.1.1(ii)). */
  setAiProcessing(on: boolean): void;
  setGeminiKey(key: string): void;
  setTheme(theme: ThemePref): void;
  setTextScale(scale: TextScale): void;
  setCalmMotion(on: boolean): void;
  setEasyFont(on: boolean): void;
  /** Collapse/expand the Wrap-Up (fatigue) banner to a one-line strip. */
  toggleFatigueCollapsed(): void;
  toggleFocusMode(): void;
  /** Today lens on/off (V2 §1). */
  setTodayLens(on: boolean): void;
  /** Persist the brain-dump draft as typed (V2 §4a-capture). */
  setDumpDraft(text: string): void;
  markPrivacyNudgeSeen(): void;
  setWeeklyReminderPref(on: boolean): void;
  setDailyNudgePref(on: boolean): void;
  setDailyAffirmationPref(on: boolean): void;
  dismissAffirmationToday(): void;
  toggleAffirmationCollapsed(): void;
  resetAll(): void;
  /** Called by the sync engine only. */
  setSyncState(partial: Partial<Pick<AppState, 'syncStatus' | 'syncEmail' | 'syncUpdatedAt' | 'syncUid' | 'syncError' | 'syncNotice' | 'appleNewAccount' | 'syncProviders' | 'lastAccount'>>): void;
  applyRemoteSnapshot(snapshot: SyncSnapshot, updatedAt: number): void;
}

/**
 * Strictly increasing creation stamps: a Magic Sort creates many tasks in
 * the same millisecond, and identical createdAt values would leave their
 * relative order ambiguous across devices. Monotonic stamps make creation
 * order total, so every device sorts identically (see compareTasks).
 */
let lastCreationStamp = 0;
function nextCreationStamp(): number {
  lastCreationStamp = Math.max(Date.now(), lastCreationStamp + 1);
  return lastCreationStamp;
}

function toTask(c: ClassifiedTask): Task {
  const now = nextCreationStamp();
  return {
    id: uid(),
    text: c.text,
    zone: c.zone,
    urgency: c.urgency,
    importance: c.importance,
    // Only materialize the field when present — Firestore rejects undefined.
    ...(c.deadlineHint !== undefined && { deadlineHint: c.deadlineHint }),
    steps: [],
    stepsGenerated: false,
    done: false,
    rolloverCount: 0,
    createdAt: now,
    editedAt: now,
  };
}

/** The network-layer consent check handed to every cloud provider
 *  (engine/aiConsent.ts). Reads the live store, so Settings → "AI
 *  processing: off" stops in-flight code paths too. */
function aiConsentCheck(): boolean {
  return aiAllowed(useStore.getState().aiConsent);
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => {
      // Provider news (identify / renewal / refund / boot refresh) arrives
      // here and NEVER reaches `subscription` raw — preserveGrant first
      // (grantGuard.ts, SEAM_REGISTRY seam 6).
      const billing = resolveBilling(
        () => get().subscription,
        (incoming) => get().applyBillingUpdate(incoming),
        (line) => get().noteBillingStep(line),
      );

      return {
        screen: 'hook' as Screen,
        onboarded: false,
        tasks: {},
        archive: [],
        weekHistory: [],
        subscription: { plan: 'free' } as Subscription,
        scansRemaining: FREE_SCANS,
        freePlansRemaining: FREE_PLANS,
        importStrategy: 'merge' as ImportStrategy,
        geminiKey: '',
        scans: [],
        paywallOpen: false,
        settingsOpen: false,
        rolloverOpen: false,
        scanPackOpen: false,
        expandingTaskId: undefined,
        providerNotice: undefined,
        aiConsent: undefined,
        aiConsentPrompt: undefined,
        importNotice: undefined,
        mergeReview: [],
        weekStartedAt: 0,
        boundWeeks: {},
        bin: [] as BinEntry[],
        tags: {},
        activeTagId: undefined,
        sortMode: 'tag' as SortMode,
        lastOpenedOn: '',
        theme: 'system' as ThemePref,
        textScale: 'default' as TextScale,
        calmMotion: false,
        easyFont: false,
        focusMode: false,
        todayLensOn: false,
        dumpDraft: '',
        weeklyReminder: false,
        dailyNudge: false,
        syncStatus: 'disabled' as SyncStatus,
        syncEmail: undefined,
        billingNotice: undefined,
        billingBusy: false,
        billingTrace: [],
        storePrices: {},
        insightFeedbackGiven: {},
        syncUpdatedAt: 0,
        lastLocalEditAt: 0,
        installedAt: Date.now(),
        reviewAskCount: 0,
        lastReviewAskAt: 0,
        privacyNudgeSeen: false,
        dailyAffirmation: true,
        affirmationDismissedOn: '',
        affirmationCollapsedOn: '',
        fatigueCollapsed: false,

        setScreen: (screen) => set({ screen }),
        setImportStrategy: (importStrategy) => set({ importStrategy }),

        attachScan: (scan) => {
          const s = get();
          const premium = isPremium(s.subscription);
          // Free tier: tokens are counted against attached photos but only
          // CONSUMED when the AI successfully reads them — a failed run
          // costs nothing (tester feedback #7, Susan: a failed scan must
          // never burn one of the three free tokens). Out of tokens →
          // offer the pack.
          if (!premium && s.scansRemaining <= s.scans.length) {
            get().openScanPack();
            return;
          }
          set({ scans: [...s.scans, scan] });
        },

        removeScan: (index) =>
          set((s) => ({ scans: s.scans.filter((_, i) => i !== index) })),

        updateScan: (index, scan) =>
          set((s) => ({ scans: s.scans.map((sc, i) => (i === index ? scan : sc)) })),

        quickAdd: (text, zone) => {
          // One task per item — "next item" separates them (engine/dictationText.ts).
          const items = splitQuickTasks(text);
          if (items.length === 0) return;
          const tags = Object.values(get().tags);
          const added: Record<string, Task> = {};
          for (const item of items) {
            const task = toTask({ text: item, zone, urgency: 50, importance: 50 });
            const known = autoTagFor(task.text, tags);
            if (known) task.tags = [known.id];
            added[task.id] = task;
          }
          set((s) => ({ tasks: { ...s.tasks, ...added }, lastLocalEditAt: Date.now() }));
        },

        runMagicSort: async (rawInput, opts) => {
          const { scans, importStrategy, geminiKey } = get();
          const onDevice = Boolean(opts?.onDevice);
          // Consent gate (5.1.2(i)): nothing is sent until the user has agreed.
          if (!onDevice && !aiAllowed(get().aiConsent)) {
            set({ aiConsentPrompt: { kind: 'sort', rawInput } });
            return;
          }
          set({ screen: 'sorting', providerNotice: undefined, importNotice: undefined, aiConsentPrompt: undefined });
          const provider = onDevice ? new LocalProvider() : resolveProvider(geminiKey, aiConsentCheck);

          const started = Date.now();
          // Watchdog of last resort (9 Oct 2026, iOS build 2.2.0 (3) froze
          // here for ever). Whatever the provider does — hang, reject — the
          // user leaves this screen: typed tasks are sorted on the device,
          // and the reason is shown verbatim (CLAUDE.md §1 fallback, §2).
          let classified: Awaited<ReturnType<typeof provider.classify>>;
          let watchdogNote: string | undefined;
          try {
            classified = await withTimeout(
              provider.classify(rawInput, scans),
              AI_TIMEOUTS.sortMs,
              'Organize',
            );
          } catch (err) {
            const why = err instanceof Error ? err.message : String(err);
            watchdogNote = `The AI didn't finish (${why}) — sorted on this device instead.`;
            classified = await new LocalProvider().classify(rawInput).catch(() => []);
          }
          if (onDevice) watchdogNote = 'Sorted on this device — nothing was sent. Turn on AI processing in Settings → Privacy & Data for AI sorting.';
          // Keep the sort animation on screen long enough to register.
          const minAnimation = 1800;
          const elapsed = Date.now() - started;
          if (elapsed < minAnimation) {
            await new Promise((r) => setTimeout(r, minAnimation - elapsed));
          }

          if (classified.length === 0) {
            const scanNeedsVision = scans.length > 0 && !provider.supportsVision;
            const aiFailed =
              scans.length > 0 &&
              (Boolean(watchdogNote) ||
                ('lastError' in provider &&
                  Boolean((provider as { lastError?: string }).lastError)));
            set({
              screen: 'dump',
              providerNotice: scanNeedsVision
                ? 'Reading paper scans needs the Gemini AI engine — add a key in Settings, or type your tasks instead.'
                : aiFailed
                  ? "The AI couldn't be reached — no scan was used. Your photos are still attached; check your connection and tap Organize again."
                  : 'No tasks detected — try adding a bit more detail.',
            });
            return;
          }

          // Items the scan saw ticked/struck through are COMPLETED work — the
          // user decides their fate (record as done / add anyway / ignore).
          // Ones matching work already recorded as done skip silently.
          const s0 = get();
          const openItems = classified.filter((c) => !c.alreadyDone);
          const knownDoneTexts = [
            ...Object.values(s0.tasks).filter((t) => t.done).map((t) => t.text),
            ...s0.archive.map((a) => a.text),
          ];
          const completedReview: UncertainMatch[] = classified
            .filter((c) => c.alreadyDone)
            .filter((c) => !knownDoneTexts.some((t) => sameItem(t, c.text)))
            .map((c) => ({ item: c, matchText: '', kind: 'completed' as const }));

          const merged =
            importStrategy === 'merge'
              ? mergeNewItems(openItems, Object.values(s0.tasks), s0.archive)
              : { fresh: openItems, alreadyOpen: 0, alreadyDone: 0, uncertain: [] };

          const tagList = Object.values(s0.tags);
          const newTasks = merged.fresh.map((c) => {
            const t = toTask(c);
            // Ongoing recognition: "Header: item" matching an existing tag
            // groups itself — next week's scan needs zero re-tagging.
            const known = autoTagFor(t.text, tagList);
            if (known) t.tags = [known.id];
            return t;
          });
          void import('../native/native').then((n) => n.hapticSuccess());
          set((s) => ({
            tasks: {
              ...(importStrategy === 'merge' ? s.tasks : {}),
              ...Object.fromEntries(newTasks.map((t) => [t.id, t])),
            },
            // Start Fresh is a bulk delete — the outgoing board goes to the
            // Bin, so "I picked the wrong option" is recoverable for 7 days.
            bin:
              importStrategy === 'merge'
                ? s.bin
                : Object.values(s.tasks).reduce((b, t) => toBin(b, t, Date.now()), s.bin),
            archive: importStrategy === 'merge' ? s.archive : [],
            scans: [],
            // Tokens are consumed HERE — on success only (see attachScan).
            scansRemaining: isPremium(s.subscription)
              ? s.scansRemaining
              : Math.max(0, s.scansRemaining - scans.length),
            screen: 'matrix',
            onboarded: true,
            // Successful organize is the ONLY thing that clears the draft
            // (V2 §4a-capture): failures and navigation keep it intact.
            dumpDraft: '',
            lastLocalEditAt: Date.now(),
            importNotice: mergeNotice(merged),
            mergeReview: [...merged.uncertain, ...completedReview],
            providerNotice:
              watchdogNote ??
              ('lastError' in provider ? (provider as { lastError?: string }).lastError : undefined),
          }));
        },

        resolveMergeReview: (index, action) =>
          set((s) => {
            const entry = s.mergeReview[index];
            if (!entry) return s;
            const rest = s.mergeReview.filter((_, i) => i !== index);
            if (action === 'skip') return { mergeReview: rest, lastLocalEditAt: Date.now() };

            if (action === 'done') {
              // "That's completed work — record it." If a matching open task
              // already exists on the board, complete THAT one; otherwise
              // create the task as done. Either way it earns week credit.
              const existing = Object.values(s.tasks).find(
                (t) => !t.done && sameItem(t.text, entry.item.text),
              );
              const now = Date.now();
              if (existing) {
                return {
                  mergeReview: rest,
                  tasks: {
                    ...s.tasks,
                    [existing.id]: { ...existing, done: true, editedAt: now },
                  },
                  archive: [
                    ...s.archive,
                    { taskId: existing.id, text: existing.text, zone: existing.zone, completedAt: now },
                  ],
                  lastLocalEditAt: now,
                };
              }
              const doneTag = autoTagFor(entry.item.text, Object.values(s.tags));
              const t = {
                ...toTask(entry.item),
                done: true,
                ...(doneTag && { tags: [doneTag.id] }),
              };
              return {
                mergeReview: rest,
                tasks: { ...s.tasks, [t.id]: t },
                archive: [
                  ...s.archive,
                  { taskId: t.id, text: t.text, zone: t.zone, completedAt: now },
                ],
                lastLocalEditAt: now,
              };
            }

            // action === 'add' — cross-device guard: if the same question was
            // already answered "add" elsewhere and synced in, don't add twice.
            const alreadyThere = Object.values(s.tasks).some(
              (t) => !t.done && sameItem(t.text, entry.item.text),
            );
            if (alreadyThere) return { mergeReview: rest, lastLocalEditAt: Date.now() };
            const t = toTask(entry.item);
            const known = autoTagFor(t.text, Object.values(s.tags));
            if (known) t.tags = [known.id];
            return {
              mergeReview: rest,
              tasks: { ...s.tasks, [t.id]: t },
              lastLocalEditAt: Date.now(),
            };
          }),

        drillDown: async (taskId) => {
          const state = get();
          const task = state.tasks[taskId];
          if (!task) return;
          if (!aiAllowed(state.aiConsent)) {
            set({ aiConsentPrompt: { kind: 'drillDown', taskId } });
            return;
          }

          // Local lapse reconciliation, not a provider answer. `reconcile`
          // returns granted accounts untouched (grantGuard.ts), so this can
          // never downgrade a lifetime tester.
          const reconciled = reconcile(state.subscription);
          if (reconciled !== state.subscription) set({ subscription: reconciled });

          if (!isPremium(reconciled)) {
            // Free sampling: the first FREE_PLANS drill-downs run in full —
            // users must EXPERIENCE the feature before being asked to pay.
            // The paywall appears only when the samples are spent.
            if (task.stepsGenerated) return;
            if (state.freePlansRemaining <= 0) {
              set({ paywallOpen: true });
              return;
            }
            set({ freePlansRemaining: state.freePlansRemaining - 1 });
          }
          if (task.stepsGenerated) return;

          set({ expandingTaskId: taskId, planNotice: undefined });
          try {
            const provider = resolveProvider(state.geminiKey, aiConsentCheck);
            const raw = await provider.expand(task.text, task.zone);
            const why = planFallbackNotice('plan', provider);
            if (why) set({ planNotice: { taskId, text: why } });
            set((s) => ({
              tasks: {
                ...s.tasks,
                [taskId]: {
                  ...s.tasks[taskId],
                  steps: raw.map((r) => ({ ...r, id: uid(), done: false })),
                  stepsGenerated: true,
                  editedAt: Date.now(),
                },
              },
              lastLocalEditAt: Date.now(),
            }));
          } finally {
            set({ expandingTaskId: undefined });
          }
        },

        addExtraStep: async (taskId) => {
          const state = get();
          const task = state.tasks[taskId];
          if (!task || !task.stepsGenerated) return;
          if (!aiAllowed(state.aiConsent)) {
            set({ aiConsentPrompt: { kind: 'addExtraStep', taskId } });
            return;
          }
          set({ expandingTaskId: taskId, planNotice: undefined });
          try {
            const provider = resolveProvider(state.geminiKey, aiConsentCheck);
            const raw = await provider.expand(
              `${task.text} — suggest one additional next step beyond: ${task.steps
                .map((s) => s.text)
                .join('; ')}`,
              task.zone,
            );
            // The last suggested step NOT already in the plan (18 Oct 2026:
            // the AI's last step repeated step 4 word for word).
            const why = planFallbackNotice('extra', provider);
            const extra = pickExtraStep(task.steps, raw);
            if (!extra) {
              set({ planNotice: { taskId, text: why ?? NO_NEW_STEP_NOTICE } });
              return;
            }
            set({ planNotice: why ? { taskId, text: why } : undefined });
            set((s) => ({
              tasks: {
                ...s.tasks,
                [taskId]: {
                  ...s.tasks[taskId],
                  steps: [...s.tasks[taskId].steps, { ...extra, id: uid(), done: false }],
                  editedAt: Date.now(),
                },
              },
              lastLocalEditAt: Date.now(),
            }));
          } finally {
            set({ expandingTaskId: undefined });
          }
        },

        updatePlan: async (taskId, note) => {
          const state = get();
          const task = state.tasks[taskId];
          if (!task || task.steps.length === 0 || !note.trim()) return;
          if (!aiAllowed(state.aiConsent)) {
            set({ aiConsentPrompt: { kind: 'updatePlan', taskId, note } });
            return;
          }

          // Local lapse reconciliation, not a provider answer (grantGuard.ts).
          const reconciled = reconcile(state.subscription);
          if (reconciled !== state.subscription) set({ subscription: reconciled });
          // Premium feature: free users keep full MANUAL plan editing; the
          // AI merge is part of the paid tier.
          if (!isPremium(reconciled)) {
            set({ paywallOpen: true });
            return;
          }

          set({ expandingTaskId: taskId, planNotice: undefined });
          try {
            const provider = resolveProvider(state.geminiKey, aiConsentCheck);
            const revised = await provider.revise(
              task.text,
              task.zone,
              task.steps.filter((st) => st.done),
              task.steps.filter((st) => !st.done),
              note,
            );
            const why = planFallbackNotice('update', provider);
            set({ planNotice: why ? { taskId, text: why } : undefined });
            set((s) => {
              const cur = s.tasks[taskId];
              if (!cur) return s; // deleted while the AI was thinking
              return {
                tasks: {
                  ...s.tasks,
                  [taskId]: {
                    ...cur,
                    // Merge against CURRENT steps: anything completed while
                    // the request was in flight is preserved too.
                    steps: applyPlanRevision(cur.steps, revised, uid),
                    stepsGenerated: true,
                    editedAt: Date.now(),
                  },
                },
                lastLocalEditAt: Date.now(),
              };
            });
          } finally {
            set({ expandingTaskId: undefined });
          }
        },

        addManualStep: (taskId, rawText) =>
          set((s) => {
            const task = s.tasks[taskId];
            const parsed = parseStepInput(rawText);
            if (!task || !parsed) return s;
            return {
              tasks: {
                ...s.tasks,
                [taskId]: {
                  ...task,
                  steps: [...task.steps, { ...parsed, id: uid(), done: false }],
                  // A task with steps HAS a plan, however it was authored.
                  stepsGenerated: true,
                  editedAt: Date.now(),
                },
              },
              lastLocalEditAt: Date.now(),
            };
          }),

        updateStep: (taskId, stepId, rawText) =>
          set((s) => {
            const task = s.tasks[taskId];
            const parsed = parseStepInput(rawText);
            if (!task || !parsed) return s;
            return {
              tasks: {
                ...s.tasks,
                [taskId]: {
                  ...task,
                  steps: task.steps.map((st) =>
                    st.id === stepId ? { ...st, text: parsed.text, minutes: parsed.minutes } : st,
                  ),
                  editedAt: Date.now(),
                },
              },
              lastLocalEditAt: Date.now(),
            };
          }),

        updateTaskText: (taskId, text) =>
          set((s) => {
            const task = s.tasks[taskId];
            const trimmed = text.replace(/\s+/g, ' ').trim();
            if (!task || !trimmed || trimmed === task.text) return s;
            return {
              tasks: {
                ...s.tasks,
                [taskId]: { ...task, text: trimmed, editedAt: Date.now() },
              },
              lastLocalEditAt: Date.now(),
            };
          }),

        updateTaskDeadline: (taskId, hint, at) =>
          set((s) => {
            const task = s.tasks[taskId];
            if (!task) return s;
            const trimmed = hint.replace(/\s+/g, ' ').trim();
            const next: Task = { ...task, editedAt: Date.now() };
            // Absent key, never undefined — Firestore rejects undefined.
            if (trimmed) {
              next.deadlineHint = trimmed;
              if (typeof at === 'number' && Number.isFinite(at)) next.deadlineAt = at;
              else delete next.deadlineAt; // free text: no guessed timestamp
            } else {
              delete next.deadlineHint;
              delete next.deadlineAt;
            }
            // A new day means a new hand-order group (15 Oct 2026, same-day
            // ordering): an old position is meaningless there, so the task
            // starts at the bottom of its new day until moved by hand.
            if (orderGroupKey(next) !== orderGroupKey(task)) delete next.manualRank;
            return {
              tasks: { ...s.tasks, [taskId]: next },
              lastLocalEditAt: Date.now(),
            };
          }),

        deleteStep: (taskId, stepId) =>
          set((s) => {
            const task = s.tasks[taskId];
            if (!task) return s;
            return {
              tasks: {
                ...s.tasks,
                [taskId]: {
                  ...task,
                  steps: task.steps.filter((st) => st.id !== stepId),
                  editedAt: Date.now(),
                },
              },
              lastLocalEditAt: Date.now(),
            };
          }),

        moveStep: (taskId, stepId, direction) =>
          set((s) => {
            const task = s.tasks[taskId];
            if (!task) return s;
            const i = task.steps.findIndex((st) => st.id === stepId);
            const j = i + direction;
            if (i < 0 || j < 0 || j >= task.steps.length) return s;
            const steps = [...task.steps];
            [steps[i], steps[j]] = [steps[j], steps[i]];
            return {
              tasks: {
                ...s.tasks,
                [taskId]: { ...task, steps, editedAt: Date.now() },
              },
              lastLocalEditAt: Date.now(),
            };
          }),

        promoteStep: (taskId, stepId) =>
          set((s) => {
            const task = s.tasks[taskId];
            if (!task) return s;
            const promotion = promoteStepFrom(task, stepId);
            if (!promotion) return s;
            const child = toTask(promotion.childSeed);
            return {
              tasks: {
                ...s.tasks,
                [taskId]: { ...task, steps: promotion.parentSteps, editedAt: Date.now() },
                [child.id]: child,
              },
              lastLocalEditAt: Date.now(),
            };
          }),

        toggleStep: (taskId, stepId) =>
          set((s) => {
            const task = s.tasks[taskId];
            if (!task) return s;
            return {
              tasks: {
                ...s.tasks,
                [taskId]: {
                  ...task,
                  steps: task.steps.map((st) =>
                    st.id === stepId ? { ...st, done: !st.done } : st,
                  ),
                  editedAt: Date.now(),
                },
              },
              lastLocalEditAt: Date.now(),
            };
          }),

        setTaskRepeat: (taskId, rule) =>
          set((s) => {
            const task = s.tasks[taskId];
            if (!task) return s;
            const next: Task = { ...task, editedAt: Date.now() };
            if (rule && isRepeatRule(rule)) {
              next.repeat = rule;
              // A repeating task needs a date to count from: none yet → the
              // first one after today (a hint written the picker's way).
              if (next.deadlineAt === undefined) {
                const d = new Date();
                const at = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0).getTime();
                next.deadlineAt = at; // starts today; the next copy counts on from here
                next.deadlineHint = hintFor(at);
              }
            } else {
              delete next.repeat; // absent key, never undefined (Firestore)
            }
            return { tasks: { ...s.tasks, [taskId]: next }, lastLocalEditAt: Date.now() };
          }),

        toggleTaskDone: (taskId) =>
          set((s) => {
            const task = s.tasks[taskId];
            if (!task) return s;
            const done = !task.done;
            // Warm native buzz on completion (no-op on web).
            if (done) void import('../native/native').then((n) => n.hapticSuccess());
            const now = Date.now();
            const tasks: Record<string, Task> = { ...s.tasks, [taskId]: { ...task, done, editedAt: now } };
            // Recurring (18 Oct 2026, engine/recurrence.ts): completing creates
            // the next copy — its id is derived from the series and the date,
            // so it is never created twice; un-completing removes it only
            // while it is untouched.
            if (done && isRepeatRule(task.repeat)) {
              const next = nextInstance(task, now, uid);
              // The copy is stamped with the completion time, which is how an
              // undo finds it again (below) even on a later day.
              if (next && !tasks[next.id]) tasks[next.id] = next;
            }
            if (!done && task.done) {
              const series = seriesId(taskId);
              for (const t of Object.values(tasks)) {
                if (
                  t.id !== taskId &&
                  seriesId(t.id) === series &&
                  t.createdAt === task.editedAt &&
                  isUntouchedInstance(t)
                ) {
                  delete tasks[t.id];
                }
              }
            }
            return {
              tasks,
              archive: done
                ? [
                    ...s.archive,
                    { taskId, text: task.text, zone: task.zone, completedAt: Date.now() },
                  ]
                : s.archive.filter((a) => a.taskId !== taskId),
              lastLocalEditAt: Date.now(),
            };
          }),

        createTag: (name, kind) => {
          const trimmed = name.replace(/\s+/g, ' ').trim();
          if (!trimmed) return undefined;
          const s = get();
          const existing = findTagByName(Object.values(s.tags), trimmed);
          if (existing) return existing.id;
          const tag: Tag = {
            id: uid(),
            name: trimmed,
            kind,
            color: nextTagColor(Object.values(s.tags)),
            createdAt: Date.now(),
            editedAt: Date.now(),
          };
          set((st) => ({
            tags: { ...st.tags, [tag.id]: tag },
            lastLocalEditAt: Date.now(),
          }));
          return tag.id;
        },

        renameTag: (tagId, name) =>
          set((s) => {
            const tag = s.tags[tagId];
            const trimmed = name.replace(/\s+/g, ' ').trim();
            if (!tag || !trimmed) return s;
            const target = findTagByName(
              Object.values(s.tags).filter((t) => t.id !== tagId),
              trimmed,
            );
            if (target) {
              // Merge: move every task to the surviving tag, retire this one.
              const { [tagId]: _gone, ...restTags } = s.tags;
              const tasks = Object.fromEntries(
                Object.entries(s.tasks).map(([id, t]) => {
                  if (!(t.tags ?? []).includes(tagId)) return [id, t];
                  const next = (t.tags ?? []).filter((x) => x !== tagId);
                  if (!next.includes(target.id)) next.push(target.id);
                  return [id, { ...t, tags: next, editedAt: Date.now() }];
                }),
              );
              return {
                tags: restTags,
                tasks,
                activeTagId: s.activeTagId === tagId ? target.id : s.activeTagId,
                lastLocalEditAt: Date.now(),
              };
            }
            return {
              tags: {
                ...s.tags,
                [tagId]: { ...tag, name: trimmed, editedAt: Date.now() },
              },
              lastLocalEditAt: Date.now(),
            };
          }),

        setTagColor: (tagId, color) =>
          set((s) => {
            const tag = s.tags[tagId];
            if (!tag || !/^#[0-9a-f]{6}$/i.test(color)) return s;
            return {
              tags: { ...s.tags, [tagId]: { ...tag, color, editedAt: Date.now() } },
              lastLocalEditAt: Date.now(),
            };
          }),

        deleteTag: (tagId) =>
          set((s) => {
            const { [tagId]: _removed, ...rest } = s.tags;
            const tasks = Object.fromEntries(
              Object.entries(s.tasks).map(([id, t]) =>
                (t.tags ?? []).includes(tagId)
                  ? [id, { ...t, tags: (t.tags ?? []).filter((x) => x !== tagId), editedAt: Date.now() }]
                  : [id, t],
              ),
            );
            return {
              tags: rest,
              tasks,
              activeTagId: s.activeTagId === tagId ? undefined : s.activeTagId,
              lastLocalEditAt: Date.now(),
            };
          }),

        setTagCore: (tagId, core) =>
          set((s) => {
            const tag = s.tags[tagId];
            if (!tag || Boolean(tag.core) === core) return s;
            return {
              tags: { ...s.tags, [tagId]: { ...tag, core, editedAt: Date.now() } },
              lastLocalEditAt: Date.now(),
            };
          }),
        dismissCoreTip: () => set({ coreTipDismissed: true }),

        handOff: (taskId, name, checkBackAt) =>
          set((s) => {
            const task = s.tasks[taskId];
            const handedTo = normaliseHandedTo(name);
            if (!task || !handedTo) return s;
            return {
              tasks: {
                ...s.tasks,
                [taskId]: { ...task, handedTo, deferredUntil: checkBackAt, editedAt: Date.now() },
              },
              handOffEditing: undefined,
              lastLocalEditAt: Date.now(),
            };
          }),
        setCheckBack: (taskId, checkBackAt) =>
          set((s) => {
            const task = s.tasks[taskId];
            if (!task) return s;
            return {
              tasks: { ...s.tasks, [taskId]: { ...task, deferredUntil: checkBackAt, editedAt: Date.now() } },
              lastLocalEditAt: Date.now(),
            };
          }),
        takeBack: (taskId) =>
          set((s) => {
            const task = s.tasks[taskId];
            if (!task) return s;
            const { handedTo: _h, deferredUntil: _d, ...rest } = task;
            return {
              tasks: { ...s.tasks, [taskId]: { ...rest, editedAt: Date.now() } },
              handOffEditing: undefined,
              lastLocalEditAt: Date.now(),
            };
          }),
        openHandOff: (taskId) => set({ handOffEditing: taskId }),

        rateWeekReview: (weekId, useful, comment) => {
          set((s) => ({ insightFeedbackGiven: { ...s.insightFeedbackGiven, [weekId]: useful } }));
          void import('../sync/firebaseSync').then((m) => m.pushInsightFeedback(weekId, useful, comment));
        },

        toggleTaskTag: (taskId, tagId) =>
          set((s) => {
            const task = s.tasks[taskId];
            if (!task || !s.tags[tagId]) return s;
            const current = task.tags ?? [];
            const tags = current.includes(tagId)
              ? current.filter((x) => x !== tagId)
              : [...current, tagId];
            return {
              tasks: { ...s.tasks, [taskId]: { ...task, tags, editedAt: Date.now() } },
              lastLocalEditAt: Date.now(),
            };
          }),

        setActiveTag: (activeTagId) => set({ activeTagId }),
        setSortMode: (sortMode) => set({ sortMode }),

        cycleTaskState: (taskId) => {
          const task = get().tasks[taskId];
          if (!task) return;
          if (task.done) {
            // done → blank: reuse toggle (removes archive entry), then clear started.
            get().toggleTaskDone(taskId);
            set((s) => {
              const t = s.tasks[taskId];
              if (!t) return s;
              const next: Task = { ...t, editedAt: Date.now() };
              delete next.startedAt;
              return { tasks: { ...s.tasks, [taskId]: next }, lastLocalEditAt: Date.now() };
            });
            return;
          }
          if (task.startedAt) {
            // in-progress → done: full completion path (archive + haptic).
            get().toggleTaskDone(taskId);
            return;
          }
          // blank → in-progress
          set((s) => {
            const t = s.tasks[taskId];
            if (!t) return s;
            return {
              tasks: { ...s.tasks, [taskId]: { ...t, startedAt: Date.now(), editedAt: Date.now() } },
              lastLocalEditAt: Date.now(),
            };
          });
        },

        cycleStepState: (taskId, stepId) =>
          set((s) => {
            const task = s.tasks[taskId];
            if (!task) return s;
            const steps = task.steps.map((st) => {
              if (st.id !== stepId) return st;
              if (st.done) {
                // done → blank
                const next: ActionStep = { ...st, done: false };
                delete next.startedAt;
                return next;
              }
              if (st.startedAt) return { ...st, done: true }; // started → done
              return { ...st, startedAt: Date.now() }; // blank → started
            });
            return {
              tasks: { ...s.tasks, [taskId]: { ...task, steps, editedAt: Date.now() } },
              lastLocalEditAt: Date.now(),
            };
          }),

        updateTaskNote: (taskId, note) =>
          set((s) => {
            const task = s.tasks[taskId];
            if (!task) return s;
            const trimmed = note.trim();
            const next: Task = { ...task, editedAt: Date.now() };
            if (trimmed) next.note = trimmed;
            else delete next.note;
            return {
              tasks: { ...s.tasks, [taskId]: next },
              lastLocalEditAt: Date.now(),
            };
          }),

        deleteTask: (taskId) =>
          set((s) => {
            const task = s.tasks[taskId];
            const { [taskId]: _removed, ...rest } = s.tasks;
            return {
              tasks: rest,
              // Never destroy on a tap: the task moves to the Bin (7-day
              // hold, restorable) and its entry tombstones the deletion
              // across devices.
              bin: task ? toBin(s.bin, task, Date.now()) : s.bin,
              lastLocalEditAt: Date.now(),
            };
          }),

        restoreFromBin: (taskId) =>
          set((s) => {
            const entry = s.bin.find((e) => e.task.id === taskId);
            // A purged entry has no payload left to restore — its text was
            // redacted when the user chose "Delete forever". It survives only
            // as a tombstone, so restoring it would put an empty task on the
            // board. Guarded here as well as hidden in the UI.
            if (!entry || entry.purgedAt) return s;
            // editedAt = now beats the tombstone in every future merge.
            const task = { ...entry.task, editedAt: Date.now() };
            return {
              tasks: { ...s.tasks, [task.id]: task },
              bin: s.bin.filter((e) => e.task.id !== taskId),
              lastLocalEditAt: Date.now(),
            };
          }),

        purgeFromBin: (taskId) =>
          set((s) => ({
            bin: purgeEntry(s.bin, taskId, Date.now()),
            lastLocalEditAt: Date.now(),
          })),

        // ⚠ FIXED 2 Sept 2026. This used to be `bin: []`, which destroyed
        // every tombstone: any device that had not yet synced still held
        // those tasks alive, and the next merge unioned them back onto the
        // board — emptying the bin silently UNDID the deletions. Now every
        // entry is redacted and kept as a tombstone until its original
        // 7-day clock runs out.
        emptyBin: () =>
          set((s) => ({
            bin: purgeAllEntries(s.bin, Date.now()),
            lastLocalEditAt: Date.now(),
          })),

        moveTaskInZone: (taskId, direction) =>
          set((s) => {
            const task = s.tasks[taskId];
            if (!task) return s;
            const siblings = Object.values(s.tasks).filter((t) => t.zone === task.zone);
            const updates = reorderInGroup(siblings, taskId, direction);
            if (!updates) return s;
            const now = Date.now();
            const tasks = { ...s.tasks };
            for (const u of updates) {
              const prev = tasks[u.id];
              if (!prev || prev.manualRank === u.manualRank) continue;
              tasks[u.id] = { ...prev, manualRank: u.manualRank, editedAt: now };
            }
            return { tasks, lastLocalEditAt: now };
          }),

        moveTask: (taskId, zone) =>
          set((s) => {
            const task = s.tasks[taskId];
            if (!task) return s;
            return {
              tasks: { ...s.tasks, [taskId]: { ...task, zone, editedAt: Date.now() } },
              lastLocalEditAt: Date.now(),
            };
          }),

        remediate: (taskId, action) =>
          set((s) => {
            const task = s.tasks[taskId];
            if (!task || action === 'keep') return s;
            const { [taskId]: _removed, ...rest } = s.tasks;
            if (action === 'split') {
              const a = toTask({
                text: `Part 1 — scope & first move: ${task.text}`,
                zone: 'zone2',
                urgency: task.urgency,
                importance: Math.max(task.importance, 60),
              });
              const b = toTask({
                text: `Part 2 — execution: ${task.text}`,
                zone: 'zone2',
                urgency: task.urgency,
                importance: Math.max(task.importance, 60),
              });
              return { tasks: { ...rest, [a.id]: a, [b.id]: b }, lastLocalEditAt: Date.now() };
            }
            const zone: ZoneId = action === 'delegate' ? 'zone3' : 'zone4';
            return {
              tasks: { ...rest, [taskId]: { ...task, zone, rolloverCount: 0, editedAt: Date.now() } },
              lastLocalEditAt: Date.now(),
            };
          }),

        startWeekRollover: () => {
          set((s) => {
            const { carried, record } = rolloverWeek(Object.values(s.tasks), s.archive);
            const week: WeekRecord = {
              id: uid(),
              label: weekLabel(),
              endedAt: Date.now(),
              ...record,
            };
            // The Notebook (V2 §11, preserve-first): bind the FULL week —
            // steps, notes, tags — before rollover discards completed work.
            // s.tags rides along as the frozen vocabulary: tags deleted
            // from the live rail later stay searchable in history.
            const bound = bindWeek(week, Object.values(s.tasks), s.tags);
            // Fire-and-forget cloud copy (own doc per week). Failures are
            // non-fatal (local copy persists) but must be visible.
            void import('../sync/firebaseSync').then((m) =>
              m.pushWeekSnapshot(bound),
            );
            return {
              tasks: Object.fromEntries(carried.map((t) => [t.id, t])),
              archive: [],
              weekHistory: [week, ...s.weekHistory].slice(0, 52),
              boundWeeks: { ...s.boundWeeks, [bound.id]: bound },
              rolloverOpen: false,
              screen: 'mastery',
              // Week boundary: a diverged device can never resurrect the
              // completed work this rollover just retired (see syncCore).
              weekStartedAt: Date.now(),
              lastLocalEditAt: Date.now(),
            };
          });
          // Review nudge at the peak moment: the Mastery screen is now
          // celebrating a closed week. Ask only after a genuinely good week
          // (policy in engine/review.ts), and let the celebration land first.
          const s = get();
          const completedThisWeek = s.weekHistory[0]?.completedCount ?? 0;
          if (
            shouldAskForReview({
              installedAt: s.installedAt,
              askCount: s.reviewAskCount,
              lastAskAt: s.lastReviewAskAt,
              completedThisWeek,
            })
          ) {
            set({ reviewAskCount: s.reviewAskCount + 1, lastReviewAskAt: Date.now() });
            setTimeout(() => {
              void import('../native/native').then((n) => n.requestStoreReview());
            }, 2500);
          }
        },

        hydrateNotebook: (snaps) =>
          set((s) => {
            const merged = { ...s.boundWeeks };
            for (const snap of snaps) if (!merged[snap.id]) merged[snap.id] = snap;
            return { boundWeeks: merged };
          }),

        buildExport: (format) => {
          const s = get();
          return exportSummary(
            format,
            Object.values(s.tasks).sort(compareTasks),
            s.archive,
            weekLabel(),
          );
        },

        subscribe: async (plan) => {
          if (get().billingBusy) return;
          set({ billingBusy: true, billingNotice: undefined });
          try {
            const sub = await billing.subscribe(plan);
            // Provider answers NEVER reach state raw — a grant outranks them
            // (grantGuard.ts, SEAM_REGISTRY seam 6).
            set((s) => ({
              subscription: preserveGrant(s.subscription, sub),
              paywallOpen: false,
              billingBusy: false,
            }));
          } catch (err) {
            // A cancelled store sheet is a decision, not a failure: stay
            // quiet. Anything else is shown verbatim (§2).
            set({
              billingBusy: false,
              billingNotice: isCancelled(err) ? undefined : describeBillingError(err),
            });
          }
        },

        applyAlphaGrant: () =>
          set((s) => ({
            subscription: grantSubscription(s.subscription),
            paywallOpen: false,
          })),

        cancelSubscription: async () => {
          if (get().billingBusy) return;
          set({ billingBusy: true, billingNotice: undefined });
          try {
            const sub = await billing.cancel();
            set((s) => ({
              subscription: preserveGrant(s.subscription, sub),
              billingBusy: false,
            }));
          } catch (err) {
            set({ billingBusy: false, billingNotice: describeBillingError(err) });
          }
        },

        purchaseScanPack: async () => {
          if (get().billingBusy) return;
          set({ billingBusy: true, billingNotice: undefined });
          try {
            const credited = await billing.purchaseScanPack();
            set((s) => ({
              scansRemaining: s.scansRemaining + credited,
              scanPackOpen: false,
              billingBusy: false,
            }));
          } catch (err) {
            set({
              billingBusy: false,
              billingNotice: isCancelled(err) ? undefined : describeBillingError(err),
            });
          }
        },

        restorePurchases: async () => {
          if (get().billingBusy) return;
          set({ billingBusy: true, billingNotice: undefined });
          try {
            const restored = await billing.restore();
            // `restore()` is the call most likely to say "free" about a
            // grant holder — it goes through the same guard as everything.
            // Null (provider has nothing to say) leaves state untouched.
            set((s) => {
              const next = preserveGrant(s.subscription, restored ?? s.subscription);
              return {
                subscription: next,
                billingBusy: false,
                billingNotice:
                  restored && isPremium(next)
                    ? 'Purchases restored — premium is active on this device.'
                    : 'No purchases found for this Google account.',
              };
            });
          } catch (err) {
            set({ billingBusy: false, billingNotice: describeBillingError(err) });
          }
        },

        noteBillingStep: (line) =>
          set((s) => ({
            billingTrace: [...s.billingTrace, `${new Date().toLocaleTimeString()} ${line}`].slice(-12),
          })),

        billingSelfTest: async () => {
          if (get().billingBusy) return;
          set({ billingBusy: true, billingNotice: undefined, billingTrace: [] });
          try {
            const ids = await runBillingSelfTest();
            set({
              billingBusy: false,
              billingNotice:
                ids === null
                  ? 'Billing is not live on this build.'
                  : `Self-test passed — ${ids.length} product(s) reachable.`,
            });
          } catch (err) {
            set({ billingBusy: false, billingNotice: describeBillingError(err) });
          }
        },

        applyBillingUpdate: (incoming) =>
          set((s) => ({ subscription: preserveGrant(s.subscription, incoming) })),

        clearBillingNotice: () => set({ billingNotice: undefined }),

        openPaywall: () => {
          set({ paywallOpen: true });
          // Store prices, if the store has them — never blocks the sheet.
          void loadStorePrices().then((prices) => {
            if (Object.keys(prices).length) set({ storePrices: prices });
          });
        },
        closePaywall: () => set({ paywallOpen: false }),
        openSettings: () => set({ settingsOpen: true }),
        closeSettings: () => set({ settingsOpen: false }),
        openRollover: () => set({ rolloverOpen: true }),
        closeRollover: () => set({ rolloverOpen: false }),
        openScanPack: () => {
          set({ scanPackOpen: true });
          void loadStorePrices().then((prices) => {
            if (Object.keys(prices).length) set({ storePrices: prices });
          });
        },
        openScanFromBoard: () =>
          set({ importStrategy: 'merge', screen: 'dump', scanOnOpen: true }),
        consumeScanOnOpen: () => {
          const pending = get().scanOnOpen === true;
          if (pending) set({ scanOnOpen: false });
          return pending;
        },
        closeScanPack: () => set({ scanPackOpen: false }),
        clearImportNotice: () => set({ importNotice: undefined }),
        clearProviderNotice: () => set({ providerNotice: undefined }),
        grantAiConsent: () => {
          const pending = get().aiConsentPrompt;
          set({ aiConsent: 'granted', aiConsentPrompt: undefined });
          if (!pending) return;
          const g = get();
          if (pending.kind === 'sort') void g.runMagicSort(pending.rawInput);
          else if (pending.kind === 'drillDown') void g.drillDown(pending.taskId);
          else if (pending.kind === 'addExtraStep') void g.addExtraStep(pending.taskId);
          else void g.updatePlan(pending.taskId, pending.note);
        },
        dismissAiConsentPrompt: () => set({ aiConsentPrompt: undefined }),
        setAiProcessing: (on) => set({ aiConsent: on ? 'granted' : 'declined', aiConsentPrompt: undefined }),
        setGeminiKey: (key) => set({ geminiKey: key }),
        setTheme: (theme) => set({ theme }),
        setTextScale: (textScale) => set({ textScale }),
        setCalmMotion: (calmMotion) => set({ calmMotion }),
        toggleFatigueCollapsed: () => set((s) => ({ fatigueCollapsed: !s.fatigueCollapsed })),
        setEasyFont: (easyFont) => set({ easyFont }),
        // Engaging focus is a TIME question, not an organizing one (§4a,
        // register #15): entering focus always lands on Date sort; the
        // selector stays available for manual override. Leaving keeps it.
        toggleFocusMode: () =>
          set((s) => ({
            focusMode: !s.focusMode,
            sortMode: !s.focusMode ? ('date' as SortMode) : s.sortMode,
          })),
        setTodayLens: (todayLensOn) => set({ todayLensOn }),
        setDumpDraft: (dumpDraft) => set({ dumpDraft }),
        markPrivacyNudgeSeen: () => set({ privacyNudgeSeen: true }),
        setDailyAffirmationPref: (dailyAffirmation) =>
          set(
            // Turning the pref ON also clears today's dismissal/collapse —
            // the toggle doubles as a deterministic "bring my note back".
            dailyAffirmation
              ? { dailyAffirmation, affirmationDismissedOn: '', affirmationCollapsedOn: '' }
              : { dailyAffirmation },
          ),
        dismissAffirmationToday: () => set({ affirmationDismissedOn: localDateKey() }),
        toggleAffirmationCollapsed: () =>
          set((s) => ({
            affirmationCollapsedOn:
              s.affirmationCollapsedOn === localDateKey() ? '' : localDateKey(),
          })),
        setWeeklyReminderPref: (weeklyReminder) => {
          set({ weeklyReminder });
          void import('../native/native').then(async (n) => {
            if (weeklyReminder && !(await n.ensureNotificationPermission())) {
              set({ weeklyReminder: false });
              return;
            }
            void n.setWeeklyReminder(weeklyReminder);
          });
        },
        setDailyNudgePref: (dailyNudge) => {
          set({ dailyNudge });
          void import('../native/native').then(async (n) => {
            if (dailyNudge && !(await n.ensureNotificationPermission())) {
              set({ dailyNudge: false });
              return;
            }
            void n.setDailyNudge(dailyNudge);
          });
        },

        resetAll: () =>
          set({
            screen: 'dump',
            tasks: {},
            archive: [],
            weekHistory: [],
            scans: [],
            paywallOpen: false,
            rolloverOpen: false,
            scanPackOpen: false,
            expandingTaskId: undefined,
            providerNotice: undefined,
            importNotice: undefined,
            mergeReview: [],
            bin: [],
            // "Erase this device" means everything on it (11 Oct 2026): tags
            // and Notebook pages too. Both live in the account's cloud copy
            // and come back on sign-in; left behind, they followed the device
            // into the next account (App Review demo account showed every tag).
            tags: {},
            boundWeeks: {},
            // An erased device is a FRESH device (11 Oct 2026): no local
            // edit clock, no sync stamp. Signing back in to the same account
            // then adopts the cloud copy instead of pushing the empty board.
            // Callers go through firebaseSync.eraseThisDevice(), which signs
            // out first.
            lastLocalEditAt: 0,
            syncUpdatedAt: 0,
            // ...and forgets which account it last used (13 Oct 2026).
            lastAccount: undefined,
            // ...and its AI consent — a fresh start asks again.
            aiConsent: undefined,
            aiConsentPrompt: undefined,
            coreTipDismissed: undefined,
            insightFeedbackGiven: {},
            handOffEditing: undefined,
          }),

        setSyncState: (partial) => set(partial),

        applyRemoteSnapshot: (snapshot, updatedAt) =>
          set((s) => ({
            tasks: snapshot.tasks,
            archive: snapshot.archive,
            weekHistory: snapshot.weekHistory,
            importStrategy: snapshot.importStrategy,
            mergeReview: snapshot.mergeReview ?? [],
            weekStartedAt: Math.max(s.weekStartedAt, snapshot.weekStartedAt ?? 0),
            tags: snapshot.tags ?? {},
            bin: snapshot.bin ?? [],
            onboarded: s.onboarded || snapshot.onboarded,
            syncUpdatedAt: updatedAt,
            // A device that just received data shouldn't sit on the hook screen.
            screen:
              s.screen === 'hook' && snapshot.onboarded ? ('matrix' as Screen) : s.screen,
          })),
      };
    },
    {
      name: 'zonein-store-v2',
      partialize: (s) => ({
        onboarded: s.onboarded,
        aiConsent: s.aiConsent,
        tasks: s.tasks,
        archive: s.archive,
        weekHistory: s.weekHistory,
        boundWeeks: s.boundWeeks,
        subscription: s.subscription,
        scansRemaining: s.scansRemaining,
        freePlansRemaining: s.freePlansRemaining,
        importStrategy: s.importStrategy,
        geminiKey: s.geminiKey,
        theme: s.theme,
        todayLensOn: s.todayLensOn,
        dumpDraft: s.dumpDraft,
        textScale: s.textScale,
        calmMotion: s.calmMotion,
        easyFont: s.easyFont,
        weeklyReminder: s.weeklyReminder,
        dailyNudge: s.dailyNudge,
        syncUpdatedAt: s.syncUpdatedAt,
        syncUid: s.syncUid,
        // Device-local "Sign in as …" memory — deliberately NOT in snapshotOf.
        lastAccount: s.lastAccount,
        lastLocalEditAt: s.lastLocalEditAt,
        installedAt: s.installedAt,
        reviewAskCount: s.reviewAskCount,
        lastReviewAskAt: s.lastReviewAskAt,
        privacyNudgeSeen: s.privacyNudgeSeen,
        dailyAffirmation: s.dailyAffirmation,
        affirmationDismissedOn: s.affirmationDismissedOn,
        affirmationCollapsedOn: s.affirmationCollapsedOn,
        // Device-local view preference — deliberately NOT in snapshotOf.
        fatigueCollapsed: s.fatigueCollapsed,
        // Weekly Insights device-local flags — deliberately NOT in snapshotOf.
        coreTipDismissed: s.coreTipDismissed,
        insightFeedbackGiven: s.insightFeedbackGiven,
        mergeReview: s.mergeReview,
        weekStartedAt: s.weekStartedAt,
        bin: s.bin,
        tags: s.tags,
        sortMode: s.sortMode,
        lastOpenedOn: s.lastOpenedOn,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // Lapsed trials drop to free on app start.
        const sub = reconcile(state.subscription);
        if (sub !== state.subscription) state.subscription = sub;
        // Returning users skip the marketing hook.
        state.screen = state.onboarded ? 'matrix' : 'hook';
        // Each day's FIRST open defaults the sort to the project view; a
        // same-day reopen keeps the user's choice, and an app left open
        // across midnight is never touched (this only runs on load).
        const today = localDateKey();
        if (state.lastOpenedOn !== today) {
          state.sortMode = 'tag';
          state.lastOpenedOn = today;
        }
        // One-time palette migration: the original palette had pairs that
        // read identical at chip size (emerald/teal, blue/indigo). Remap
        // known-old colors to their better-separated successors.
        const OLD_TO_NEW: Record<string, string> = {
          '#3b82f6': '#2563eb', '#10b981': '#16a34a', '#f59e0b': '#ea580c',
          '#8b5cf6': '#7c3aed', '#ec4899': '#db2777', '#14b8a6': '#0891b2',
          '#f97316': '#ca8a04', '#6366f1': '#64748b',
        };
        for (const tag of Object.values((state.tags ?? {}) as Record<string, Tag>)) {
          const next = OLD_TO_NEW[tag.color];
          if (next) {
            tag.color = next;
            tag.editedAt = Date.now();
          }
        }
        // Bin: purge entries past the 7-day retention on every app start
        // (also covers stores persisted before the bin existed).
        state.bin = purgeBin(state.bin ?? [], Date.now());
      },
    },
  ),
);

/** Derived helper: fatigued tasks needing the rollover audit. */
export function selectFatigued(tasks: Record<string, Task>): Task[] {
  return fatiguedTasks(Object.values(tasks));
}

export { isPremium, trialMsRemaining } from '../billing/mockBilling';
